import React, { useState, useEffect, useMemo } from 'react';
import { MarkdownRenderer } from "../MarkdownRenderer";
import Traces from "../../../pages/Traces";
import { TimeRangeType, TimeWindow } from "../TimeSeriesTypes";
import { DataTable } from "../../ui/datatable/datatable";
import { ColumnDef } from "@tanstack/react-table";
import { Trace, getIndicatorColorForStatusCode } from "../../../pages/Traces";
import { DateTimePrecision, displayDateTimeWithSelectedFormat } from "../../../utility/displayDateTime";
import {DefaultToolCallRenderer, ToolRendererComponent} from "../ToolRenderers";

// Message interface for the TracesToolRenderer
interface Message {
    role: 'user' | 'assistant' | 'system' | 'tool';
    content: string;
    tool_name?: string;
    tool_call?: boolean;
    tool_call_id?: string;
    tool_call_function_name?: string;
    tool_call_function_args?: string;
    tool_call_response_id?: string;
    tool_call_response_content?: string;
    screenshot?: string;
}

// ToolRendererProps interface
export interface ToolRendererProps {
    message: Message;
    isToolCall: boolean;
}

// Interface that matches the GetTracesHandlerArgs structure
interface GetTracesRequest {
    time_config: {
        type: TimeRangeType;
        time_period?: number;
        time_window?: TimeWindow;
        start_time?: string;
        end_time?: string;
    };
    serviceNames?: string[];
    filters?: { [key: string]: string[] };
    excludeFilters?: { [key: string]: string[] };
    regexes?: string[];
    excludeRegexes?: string[];
    environments?: string[];
}

// Helper function to extract HTTP method or DB operation
function getVerb(trace: Trace): string {
    let method = trace.spanAttributes["http.method"];
    if (method === undefined || method === "") {
        // Use a type assertion since the SpanAttributes interface doesn't include db.operation
        method = (trace.spanAttributes as any)["db.operation"];
    }
    return method || "";
}

// Helper function to extract endpoint or DB statement
function getEndpoint(trace: Trace): string {
    let endpoint = trace.spanAttributes["http.url"] || trace.spanName;
    
    // Try to extract the path component of the URL
    if (endpoint && endpoint.includes('://')) {
        try {
            const url = new URL(endpoint);
            endpoint = url.pathname;
        } catch (e) {
            // If parsing fails, keep the original value
        }
    }
    
    return endpoint || "";
}

// Helper function to get status code
function getStatusCode(trace: Trace): string {
    let statusCode = trace.spanAttributes["http.status_code"];
    if (!statusCode) {
        statusCode = trace.statusCode;
    }
    return statusCode || "";
}

// Helper function to format duration
function formatDuration(durationMicros: number): string {
    if (durationMicros < 1000) {
        return `${durationMicros.toFixed(2)}µs`;
    } else if (durationMicros < 1000000) {
        return `${(durationMicros / 1000).toFixed(2)}ms`;
    } else {
        return `${(durationMicros / 1000000).toFixed(2)}s`;
    }
}

// Tool renderer for traces data
export const TracesToolRenderer: ToolRendererComponent = ({ message, isToolCall }: ToolRendererProps) => {
    const [traces, setTraces] = useState<Trace[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    
    // We want to handle the tool call response to show parsed traces in a datatable
    const hasToolCallResponse = !!message.tool_call_response_content;
    const hasToolCallArgs = !!message.tool_call_function_args;
    
    // Setup columns for DataTable with proper accessors for filtering
    const columns: ColumnDef<any>[] = [
        {
            id: "time",
            header: "Time",
            cell: ({ row }) => {
                return (
                    <span>
                        {displayDateTimeWithSelectedFormat(
                            new Date(row.original.time),
                            [DateTimePrecision.Month, DateTimePrecision.Day, DateTimePrecision.Hours, DateTimePrecision.Minutes, DateTimePrecision.Seconds]
                        )}
                    </span>
                );
            },
        },
        {
            id: "serviceName",
            header: "Service",
            accessorKey: "serviceName",
            cell: ({ row }) => <span>{row.original.serviceName}</span>,
        },
        {
            id: "clientName",
            header: "Client",
            accessorKey: "clientName",
            cell: ({ row }) => <span>{row.original.clientName}</span>,
        },
        {
            id: "method",
            header: "Method",
            accessorKey: "method",
            cell: ({ row }) => {
                const method = row.original.method;
                return (
                    <span className={`font-mono ${method ? 'px-1.5 py-0.5 bg-backgroundlight rounded text-xs' : ''}`}>
                        {method}
                    </span>
                );
            },
        },
        {
            id: "endpoint",
            header: "Endpoint",
            accessorKey: "endpoint",
            cell: ({ row }) => (
                <div className="max-w-[800px] overflow-x-auto">
                    <div className="whitespace-pre-wrap break-words min-w-[200px]">
                        {row.original.endpoint}
                    </div>
                </div>
            ),
        },
        {
            id: "statusCode",
            header: "Status",
            accessorKey: "statusCode",
            cell: ({ row }) => {
                const statusCode = row.original.statusCode;
                const colorClass = getIndicatorColorForStatusCode(statusCode);
                return (
                    <div className="flex items-center">
                        <div className={`${colorClass} w-2 h-4 mr-2 rounded`}></div>
                        <span>{statusCode}</span>
                    </div>
                );
            },
        },
        {
            id: "duration",
            header: "Duration",
            accessorKey: "formattedDuration",
            cell: ({ row }) => <span>{row.original.formattedDuration}</span>,
        },
    ];

    // Parse traces from the tool call response when available
    useEffect(() => {
        if (hasToolCallResponse) {
            setIsLoading(true);
            try {
                const responseData = JSON.parse(message.tool_call_response_content || '{}');
                
                // Check if the response contains traces
                if (responseData && Array.isArray(responseData.traces)) {
                    // Transform traces to match the interface and prepare for filtering
                    const parsedTraces = responseData.traces.map((trace: any) => {
                        // Extract data for easier filtering
                        const method = getVerb(trace as Trace);
                        const endpoint = getEndpoint(trace as Trace);
                        const statusCode = getStatusCode(trace as Trace);
                        const formattedDuration = formatDuration(trace.duration / 1000 || 0);
                        
                        return {
                            ...trace,
                            method,
                            endpoint,
                            statusCode,
                            formattedDuration,
                            serviceName: trace.displayServiceName || trace.serviceName || '',
                            clientName: trace.displayClientName || trace.clientName || ''
                        };
                    });
                    
                    // Sort by time, newest first
                    parsedTraces.sort((a: any, b: any) => b.time - a.time);
                    
                    setTraces(parsedTraces);
                }
            } catch (error) {
                console.error('Error parsing traces from response:', error);
            } finally {
                setIsLoading(false);
            }
        }
    }, [message.tool_call_response_content, hasToolCallResponse]);
    
    // Define filters for the DataTable
    const tableFilters = useMemo(() => [], []);

    // If this is just the tool call with args but no response yet, render the original component
    if (!hasToolCallResponse && hasToolCallArgs) {
        let tracesRequest: GetTracesRequest | null = null;
        try {
            tracesRequest = JSON.parse(message.tool_call_function_args || '{}');
        } catch (error) {
            console.error('Error parsing traces request data:', error);
            return null;
        }

        // Validate request
        if (!tracesRequest || !tracesRequest.time_config) {
            return null;
        }

        return (
            <DefaultToolCallRenderer message={message}/>
        );
    }

    // If we have a tool call response, render the traces in a datatable
    if (hasToolCallResponse) {
        return (
            <div className="w-full">
                <MarkdownRenderer content={message.content} />
                <div className="border rounded overflow-hidden">
                    <DataTable
                        columns={columns}
                        data={traces}
                        tableTitle="Traces"
                        filters={tableFilters}
                        textFilteringColumnName="endpoint"
                        textFilteringPlaceholder="Search endpoints..."
                        isLoading={isLoading}
                        showPagination={true}
                        removeBorder={true}
                        emptyStateMessage={<div className="p-4 text-center">No traces found</div>}
                    />
                </div>
            </div>
        );
    }

    return null;
}

// Static method to determine if this renderer should render a message
TracesToolRenderer.shouldRender = (message: Message): boolean => {
    // Only render if it's a tool call or a tool response for get_traces
    if (message.tool_call_function_name === 'get_traces') {
        return true;
    }
    if (message.tool_call_response_id && message.tool_name === 'get_traces') {
        return true;
    }
    return false;
}; 